/**
 * RTDB-Enhanced NBA Service
 * Wraps NBAService with Firebase Realtime Database caching
 * Maintains exact API compatibility while adding caching layer
 */

import baseNBAService from '../../../../components/dashboards/services/nbaService';
import nbaRTDBCache from './rtdbCache';
import {
  CACHE_PATHS,
  getCacheKey,
  TTL_CONFIG,
  logCache
} from './rtdbConfig';

class RTDBNBAService {
  constructor() {
    // Use the imported instance directly (it's already instantiated)
    this.baseService = baseNBAService;

    // Proxy all direct property access to base service
    this.baseURL = this.baseService.baseURL;
    this.apiKey = this.baseService.apiKey;
    this.debug = this.baseService.debug;
  }

  /**
   * Enhanced getGames with RTDB caching
   * This is the core method that all other game methods use
   */
  async getGames(id = null, date = null, league = null, season = null, team = null, h2h = null, live = null) {
    // Build cache key based on parameters
    let cachePath = null;
    let ttl = TTL_CONFIG.DEFAULT;

    if (live === 'all') {
      // Live games - very short cache
      cachePath = `${CACHE_PATHS.GAMES_LIVE}/all`;
      ttl = TTL_CONFIG.LIVE_GAMES;
    } else if (date && !team && !h2h) {
      // Date-based query without team filter
      const cacheKey = `all_${season || 'current'}`;
      cachePath = `${CACHE_PATHS.GAMES_DATES}/${date}/${cacheKey}`;
      ttl = TTL_CONFIG.DATE_GAMES;
    } else if (date && team) {
      // Date-based query with team filter
      const cacheKey = `team_${team}_${season || 'current'}`;
      cachePath = `${CACHE_PATHS.GAMES_DATES}/${date}/${cacheKey}`;
      ttl = TTL_CONFIG.DATE_GAMES;
    } else if (id) {
      // Single game query - cache individually
      cachePath = `${CACHE_PATHS.GAMES}/single/${id}`;
      ttl = TTL_CONFIG.DEFAULT;
    }

    // Try to get from cache if we have a cache path
    if (cachePath) {
      try {
        const cachedData = await nbaRTDBCache.get(cachePath);
        if (cachedData) {
          logCache('hit', `Games from cache: ${cachePath}`);
          return cachedData;
        }
      } catch (error) {
        logCache('error', 'Cache read failed, falling back to API', error);
      }
    }

    // Not in cache or no cache path, fetch from API
    logCache('miss', 'Fetching games from API', { id, date, league, season, team, h2h, live });

    try {
      const response = await this.baseService.getGames(id, date, league, season, team, h2h, live);

      // Cache the response if we have a cache path
      if (cachePath && response) {
        // Use smart TTL for game collections
        if (response.response && Array.isArray(response.response)) {
          await nbaRTDBCache.setGames(cachePath, response.response, ttl);
        } else {
          await nbaRTDBCache.set(cachePath, response, ttl);
        }
      }

      return response;
    } catch (error) {
      logCache('error', 'API call failed', error);
      throw error;
    }
  }

  /**
   * Get today's NBA games with caching (uses ET timezone)
   */
  async getTodayGames(league = 12) {
    // Use the base service's getTodayDate which now uses ET timezone
    const today = this.baseService.getTodayDate();
    return await this.getGames(null, today, league);
  }

  /**
   * Get live NBA games with caching
   */
  async getLiveGames() {
    return await this.getGames(null, null, 12, null, null, null, 'all');
  }

  /**
   * Get standings with caching
   */
  async getStandings(league = 'standard', season = null, team = null, conference = null, division = null) {
    // Build cache key
    const cacheKey = getCacheKey('standings', league, season || 'current', conference || 'all', division || 'all');
    const cachePath = `${CACHE_PATHS.STANDINGS}/${cacheKey}`;
    const ttl = TTL_CONFIG.STANDINGS;

    // Try cache first
    try {
      const cachedData = await nbaRTDBCache.get(cachePath);
      if (cachedData) {
        logCache('hit', `Standings from cache: league ${league}, season ${season}`);
        return cachedData;
      }
    } catch (error) {
      logCache('error', 'Cache read failed for standings', error);
    }

    // Fetch from API
    logCache('miss', 'Fetching standings from API');

    try {
      const response = await this.baseService.getStandings(league, season, team, conference, division);

      // Cache the response
      if (response) {
        await nbaRTDBCache.set(cachePath, response, ttl);
      }

      return response;
    } catch (error) {
      logCache('error', 'API call failed for standings', error);
      throw error;
    }
  }

  /**
   * Get players with caching
   */
  async getPlayers(id = null, name = null, team = null, season = null) {
    if (id) {
      // Single player - don't cache
      return await this.baseService.getPlayers(id, name, team, season);
    }

    // Team players - cache
    const cacheKey = getCacheKey('players', team || 'all', season || 'current');
    const cachePath = `${CACHE_PATHS.PLAYERS}/${cacheKey}`;
    const ttl = TTL_CONFIG.PLAYERS;

    // Try cache first
    try {
      const cachedData = await nbaRTDBCache.get(cachePath);
      if (cachedData) {
        logCache('hit', `Players from cache: team ${team}, season ${season}`);
        return cachedData;
      }
    } catch (error) {
      logCache('error', 'Cache read failed for players', error);
    }

    // Fetch from API
    logCache('miss', 'Fetching players from API');

    try {
      const response = await this.baseService.getPlayers(id, name, team, season);

      // Cache the response
      if (response && team) {
        await nbaRTDBCache.set(cachePath, response, ttl);
      }

      return response;
    } catch (error) {
      logCache('error', 'API call failed for players', error);
      throw error;
    }
  }

  /**
   * Get teams with caching
   */
  async getTeams(id = null, league = 'standard', season = null, name = null, code = null) {
    if (id || name || code) {
      // Specific team query - don't cache
      return await this.baseService.getTeams(id, league, season, name, code);
    }

    // League teams - cache
    const cacheKey = getCacheKey('teams', league, season || 'current');
    const cachePath = `${CACHE_PATHS.TEAMS}/${cacheKey}`;
    const ttl = TTL_CONFIG.TEAMS;

    // Try cache first
    try {
      const cachedData = await nbaRTDBCache.get(cachePath);
      if (cachedData) {
        logCache('hit', `Teams from cache: league ${league}, season ${season}`);
        return cachedData;
      }
    } catch (error) {
      logCache('error', 'Cache read failed for teams', error);
    }

    // Fetch from API
    logCache('miss', 'Fetching teams from API');

    try {
      const response = await this.baseService.getTeams(id, league, season, name, code);

      // Cache the response
      if (response) {
        await nbaRTDBCache.set(cachePath, response, ttl);
      }

      return response;
    } catch (error) {
      logCache('error', 'API call failed for teams', error);
      throw error;
    }
  }

  // Utility methods - pass through to base service
  getTodayDate() {
    return this.baseService.getTodayDate();
  }

  getDefaultGameDate() {
    return this.baseService.getDefaultGameDate();
  }

  getCurrentSeason() {
    return this.baseService.getCurrentSeason();
  }

  getAvailableSeasons() {
    return this.baseService.getAvailableSeasons();
  }

  getGameStatus(game) {
    return this.baseService.getGameStatus(game);
  }

  // Multi-season games fetching (like NFL)
  async getGamesForSeasons(date = null, seasons = [2025, 2024, 2023], league = 12, team = null, timezone = 'America/New_York') {
    return await this.baseService.getGamesForSeasons(date, seasons, league, team, timezone);
  }

  isGameLive(game) {
    return this.baseService.isGameLive(game);
  }

  isGameFinished(game) {
    return this.baseService.isGameFinished(game);
  }

  formatTime(datetime) {
    return this.baseService.formatTime(datetime);
  }

  getFullDateTime(datetime) {
    return this.baseService.getFullDateTime(datetime);
  }

  // Cache management methods
  clearCache() {
    // Clear both in-memory and RTDB cache
    this.baseService.clearCache();
    nbaRTDBCache.clear(CACHE_PATHS.ROOT);
    logCache('info', 'All caches cleared (service + RTDB)');
  }

  async getCacheStats() {
    return await nbaRTDBCache.getStats();
  }

  // Manual API key setting (pass through)
  setApiKey(key) {
    this.baseService.setApiKey(key);
    this.apiKey = key;
  }
}

// Create singleton instance
const rtdbNBAService = new RTDBNBAService();

// Export as default to match NBAService usage
export default rtdbNBAService;

// Also export as named for explicit usage
export { rtdbNBAService };

// Browser debugging helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.NBARTDBService = {
    instance: rtdbNBAService,
    getCacheStats: () => rtdbNBAService.getCacheStats(),
    clearCache: () => rtdbNBAService.clearCache()
  };
}
